import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, LifeBuoy } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SupportForm } from "./SupportForm";

const HIDDEN = ["/invite/", "/pulse/share/", "/reset-password/"];
const STARTERS = {
  in: ["How do I set up my profile?", "How does the Vision Pulse work?", "How do I invite my team?"],
  out: ["What does VizionCast do?", "How much does it cost?", "Is there a free plan?"],
};

function newSession() {
  const id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  localStorage.setItem("vizi_session", id);
  return id;
}

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#0033FF] text-white" : "bg-zinc-100 text-zinc-800"}`}>{m.content.replace(/\*\*(.+?)\*\*/g, "$1")}</div>
  </div>
);

export const ViziWidget = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [session, setSession] = useState(() => localStorage.getItem("vizi_session") || newSession());
  const endRef = useRef(null);
  const signedIn = !!user;

  useEffect(() => { setMsgs([]); setShowForm(false); setSession(newSession()); }, [signedIn]);
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("vizi:open", h);
    return () => window.removeEventListener("vizi:open", h);
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy, showForm]);

  if (HIDDEN.some((p) => pathname.startsWith(p))) return null;

  const ask = async (text) => {
    const t = text.trim();
    if (!t || busy) return;
    setInput(""); setMsgs((m) => [...m, { role: "user", content: t }]); setBusy(true);
    try {
      const { data } = await api.post("/vizi/chat", { session_id: session, message: t, page: pathname });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.offer_support) setShowForm(true);
    } catch (e) {
      const d = e.response?.data?.detail;
      setMsgs((m) => [...m, { role: "assistant", content: typeof d === "string" ? d : "Sorry, I couldn't answer just now." }]);
      setShowForm(true);
    } finally { setBusy(false); }
  };

  const firstName = user?.name?.split(" ")[0];
  const isLanding = pathname === "/" && !user;

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="vizi-open-btn" aria-label="Ask Vizi"
          className={`fixed bottom-5 right-5 z-40 flex items-center gap-2 px-4 py-3 text-sm font-bold shadow-xl transition-transform hover:-translate-y-0.5 ${isLanding ? "bg-white text-[#0A0A0B]" : "bg-[#0033FF] text-white"}`}>
          <MessageCircle className="w-4 h-4" /> Ask Vizi
        </button>
      )}
      {open && (
        <div className="fixed z-50 bottom-0 right-0 sm:bottom-5 sm:right-5 w-full sm:w-[380px] h-[85vh] sm:h-[560px] bg-white border border-zinc-200 shadow-2xl flex flex-col" data-testid="vizi-panel">
          <div className="flex items-center justify-between px-4 h-14 bg-[#0A0A0B] text-white shrink-0">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 bg-[#0033FF] flex items-center justify-center font-black text-sm">V</span>
              <div><div className="font-black tracking-tight leading-none">Vizi</div><div className="text-[10px] text-zinc-400 mt-0.5">Your VizionCast guide</div></div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="vizi-close-btn" aria-label="Close"><X className="w-4 h-4 text-zinc-400 hover:text-white" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="vizi-messages">
            <Bubble m={{ role: "assistant", content: `Hi${firstName ? ` ${firstName}` : ""}! I'm Vizi. Ask me anything about ${signedIn ? "using " : ""}VizionCast.` }} />
            {msgs.length === 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {STARTERS[signedIn ? "in" : "out"].map((q, i) => (
                  <button key={q} onClick={() => ask(q)} data-testid={`vizi-starter-${i}`}
                    className="text-xs border border-zinc-300 px-2.5 py-1.5 hover:border-[#0033FF] hover:text-[#0033FF] transition-colors">{q}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => <Bubble key={i} m={m} />)}
            {busy && <div className="flex gap-1 px-3 py-3 bg-zinc-100 w-fit" data-testid="vizi-typing">{[0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 120}ms` }} />)}</div>}
            {showForm && <SupportForm sessionId={session} source="vizi" />}
            <div ref={endRef} />
          </div>
          <div className="border-t border-zinc-200 p-3 shrink-0">
            <form onSubmit={(e) => { e.preventDefault(); ask(input); }} className="flex gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Vizi…" maxLength={2000} data-testid="vizi-input"
                className="flex-1 px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#0033FF]" />
              <button disabled={busy || !input.trim()} data-testid="vizi-send-btn" aria-label="Send"
                className="bg-[#0033FF] text-white px-3 hover:bg-zinc-900 disabled:opacity-40"><Send className="w-4 h-4" /></button>
            </form>
            {!showForm && (
              <button onClick={() => setShowForm(true)} data-testid="vizi-contact-support" className="mt-2 text-[11px] text-zinc-500 hover:text-[#0033FF] inline-flex items-center gap-1">
                <LifeBuoy className="w-3 h-3" /> Need a person? Contact support
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};
